"use client";

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetProgress,
  getListImportsQueryKey,
} from "@/lib/api/generated";

/**
 * Single source of truth for "are imports finishing?" — mounted once per
 * job (in the job layout) and:
 *
 *  1. Polls `/progress` adaptively: 1.5s while anything is in flight, stops
 *     when `processing === 0` (no idle-request spam).
 *  2. Fans out cache invalidations whenever any count changes so every
 *     surface (pipeline table, header progress bar, import sheet tabs)
 *     converges on the same truth within one heartbeat.
 *
 * Other components that need progress data should call `useGetProgress`
 * with the same `jobId` — TanStack dedupes observers, so no extra HTTP.
 */
export function useImportProgressSync(
  jobId: string | undefined,
  atsImporting = false,
  // When the pipeline SSE stream is live it pushes import_progress into this
  // query's cache, so stop polling. If the stream dies the layout flips this
  // back to false (via the heartbeat watchdog) and polling resumes.
  streamConnected = false,
) {
  const queryClient = useQueryClient();
  const lastSignatureRef = useRef<string | null>(null);

  const query = useGetProgress(jobId ?? "", {
    query: {
      enabled: !!jobId,
      refetchInterval: (q) => {
        if (streamConnected) return false;
        if (atsImporting) return 1500;
        const data = q.state.data;
        if (!data) return 1500;
        return data.processing > 0 ? 1500 : false;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    },
  });

  useEffect(() => {
    if (!jobId || !query.data) return;
    const { total, processing, successful, failed } = query.data;
    // Tuple of counts — any transition (file finishes, file fails, new
    // file queued) bumps the signature and kicks off invalidation.
    const signature = `${total}:${processing}:${successful}:${failed}`;

    // Skip the first read — we only care about *transitions*, not the
    // initial snapshot (which is the baseline truth already in the cache).
    if (lastSignatureRef.current === null) {
      lastSignatureRef.current = signature;
      return;
    }
    if (lastSignatureRef.current === signature) return;
    lastSignatureRef.current = signature;

    queryClient.invalidateQueries({ queryKey: ["pipeline", jobId] });
    queryClient.invalidateQueries({
      queryKey: getListImportsQueryKey(jobId),
    });
  }, [jobId, query.data, queryClient]);

  return query;
}
